// @flow
import * as React from 'react';
import { Box } from 'grommet';
import { injectIntl, IntlShape } from 'react-intl';
import { withRouter } from 'react-router-dom';
import type { Match, History } from 'react-router-dom';
import type { LeafletContext } from 'react-leaflet';
import { withLeaflet } from 'react-leaflet';
import point14Messages from 'esm-web-pro/messages/points14Messages';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import PortIcon from 'esm-web-pro/components/layout/sidePanel/icons/PortIcon';
import Point14Attributes from 'esm-web-pro/containers/pages/network/objects/point14/Point14Attributes';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import Point14Actions from 'esm-web-pro/containers/pages/network/objects/point14/Point14Actions';
import OpticalPortContent from 'esm-web-pro/containers/pages/network/objects/opticalport/OpticalPortContent';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import SidePane from 'esm-web-pro/components/layout/SidePane';

type Props = {
    panelId: String,
    object: Object,
    intl: IntlShape,
    map: { is_draft: boolean },
    match: Match,
    history: History,
    updateObject: Function,
    resource: string,
    editor: BaseEditor,
    leaflet: LeafletContext,
}

const OpticalPort = (props: Props) => {
    const {
        object: opticalPort, intl: { formatMessage }, map: networkMap, match, history,
        updateObject, resource, editor, leaflet: { map }, panelId,
    } = props;

    const { fiber, port, circuit_path: circuit } = opticalPort;

    const point11 = opticalPort.container.container;
    let number = point11.sequence;
    if (point11.type === 'pop' && point11.alias) {
        number = point11.alias;
    }
    const focusPoint = React.useCallback(
        UseHighlightPoint(point11.coordinates.coordinates, number), [point11],
    );

    const getPointCoords = React.useCallback(() => [...opticalPort.container.container.coordinates.coordinates]
        .reverse(),
    [opticalPort]);

    const [highlightFiberFunction] = useHighlightFiber();

    const handleView = React.useCallback(() => {
        editor.unregisterAllLoadingCallback();
        const pointCoords = getPointCoords();

        if (circuit && (fiber || port?.fiber)) {
            const marchinAntsFiber = fiber || port.fiber;
            const [callbacks, circuitPath] = highlightFiberCircuit(
                circuit.path, marchinAntsFiber, map, highlightFiberFunction, pointCoords,
            );
            editor.registerMultipleLoadingCallbacks(callbacks);

            if (map) {
                map.highlightedGeometry = {
                    bounds: circuitPath, center: pointCoords,
                };
                map.flyToBoundsCenter(circuitPath, pointCoords, false);
            }
        } else if (map) {
            editor.registerLoadingCallback(focusPoint);
            map.highlightedGeometry = {
                ...map.highlightedGeometry, center: pointCoords,
            };
            map.panTo(pointCoords);
        }
        editor.reloadFeatures();
    }, [highlightFiberFunction, map, editor, circuit, fiber, focusPoint, getPointCoords, port]);

    React.useEffect(() => {
        handleView();

        return (() => {
            editor.unregisterAllLoadingCallback();
        });
    }, [editor, handleView]);

    const navigateBack = () => {
        props.history.push(props.match.url.split('/layer')[0]);
    };

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={opticalPort.sequence}
            titleLabel={formatMessage(point14Messages.opticalport)}
            titleIcon={<PortIcon />}
            titleOnView={handleView}
            actions={<NavigateBackButton />}
        >
            <Box height="100%" justify="between">
                <Box flex>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <Point14Attributes point14={opticalPort} url={match.url} onView={handleView} />
                        <HorizontalLine color="grey" />
                        <Box direction="row">
                            <Point14Actions
                                point14={opticalPort}
                                map={networkMap}
                                updateObject={updateObject}
                                resource={resource}
                                onEdit={updateObject}
                                onView={handleView}
                                editor={editor}
                            />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                    <BottomContainer>
                        <OpticalPortContent
                            opticalPort={opticalPort}
                            map={networkMap}
                            editor={editor}
                            updateObject={updateObject}
                            pointCoords={getPointCoords()}
                        />
                    </BottomContainer>
                </Box>
            </Box>
        </SidePane>
    );
};

export default withLeaflet(withRouter(injectIntl(OpticalPort)));
